import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/searchable-select.css';
import useIsMobile from '../hooks/useIsMobile';

const Select = ({ options, onSelect, placeholder, value, style }) => {
    const [isOpen, setIsOpen] = useState(false);
    const isMobile = useIsMobile();
    const wrapperRef = useRef(null);
    const clickSource = useRef(null); // Track click origin

    const selectedOption = options.find(opt => opt.value === value)?.label || '';

    const handleClickOutside = useCallback((event) => {
        if (wrapperRef.current &&
            !wrapperRef.current.contains(event.target) &&
            !isMobile &&
            isOpen) {
            setIsOpen(false);
        }
    }, [isMobile, isOpen]);

    useEffect(() => {
        if (isOpen && !isMobile) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, isMobile, handleClickOutside]);

    const handleSelect = (option) => {
        onSelect(option);
        setIsOpen(false);
        clickSource.current = 'dropdown'; // Mark selection origin
    };

    const handleInputClick = (event) => {
        if (clickSource.current !== 'dropdown') {
            setIsOpen(prev => !prev);
        }
        clickSource.current = 'input';
        event.stopPropagation();
    };

    // Reset click source when dropdown closes
    useEffect(() => {
        if (!isOpen) {
            const timeout = setTimeout(() => {
                clickSource.current = null;
            }, 100);
            return () => clearTimeout(timeout);
        }
    }, [isOpen]);

    return (
        <div className="searchable-select" ref={wrapperRef} style={style}>
            <div className="input-with-clear">
                <input
                    type="text"
                    placeholder={placeholder}
                    value={selectedOption}
                    readOnly
                    className="non-searchable-input"
                    onClick={handleInputClick}
                />
            </div>

            {isOpen && !isMobile && (
                <ul className="dropdown">
                    {options.map((option) => (
                        <li
                            key={option.value}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelect(option);
                            }}
                        >
                            {option.label}
                        </li>
                    ))}
                </ul>
            )}

            {isOpen && isMobile && (
                <div className="mobile-modal">
                    <div className="modal-header">
                        <button onClick={() => setIsOpen(false)} className="btn btn--danger">
                            Cancel
                        </button>
                    </div>
                    <ul className="modal-list">
                        {options.map((option) => (
                            <li
                                key={option.value}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelect(option);
                                }}
                            >
                                {option.label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default Select;